import React from 'react';
import {
  TrialTrackerSearchResult,
  registryForNctId,
  statusColorClasses,
  formatScrapeDate,
} from '../../utils/trialTrackerTimeline';
import TrackerLoadingPanel from './TrackerLoadingPanel';

interface Props {
  results: TrialTrackerSearchResult[];
  loading: boolean;
  error: string | null;
  summary?: string | null;
  query: string;
  selectedNct?: string | null;
  onSelect: (nctId: string) => void;
  onFilterBySponsor?: (sponsor: string) => void;
  /** Override the empty-search loading copy */
  loadingMessage?: string;
  loadingDetail?: string;
}

function formatDate(val?: string | null): string | null {
  if (!val) return null;
  return formatScrapeDate(val);
}

const TrackerSearchResults: React.FC<Props> = ({
  results,
  loading,
  error,
  summary,
  query,
  selectedNct,
  onSelect,
  onFilterBySponsor,
  loadingMessage,
  loadingDetail,
}) => {
  const hasQuery = query.trim().length >= 2;
  if (loading && results.length === 0 && !error) {
    return (
      <TrackerLoadingPanel
        message={loadingMessage || (hasQuery ? `Searching for “${query.trim()}”…` : 'Loading trials…')}
        detail={
          loadingDetail
          || (hasQuery
            ? 'Checking the Ruby database and ClinicalTrials.gov. This can take a few seconds.'
            : undefined)
        }
      />
    );
  }

  if (error && results.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-rose-600 px-6 text-center">
        {error}
      </div>
    );
  }

  // Followed-only (and similar) can pass results with an empty query — still show them.
  if (!hasQuery && results.length === 0) {
    const isFollowedEmpty = Boolean(summary);
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-sm font-semibold text-tracker-ink/80">
            {isFollowedEmpty ? 'No trials to show' : 'Search trials'}
          </div>
          <div className="text-xs text-tracker-muted mt-1 max-w-sm">
            {summary || 'Use the sidebar to search our database and ClinicalTrials.gov by asset, indication, or NCT.'}
          </div>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-sm font-semibold text-tracker-ink/80">No matching trials</div>
          <div className="text-xs text-tracker-muted mt-1">
            {hasQuery
              ? 'Try a different term or search mode.'
              : 'No trials match the current filters (e.g. Trial Status). Widen filters or clear Recruiting-only.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-5 relative">
      {loading && results.length > 0 && (
        <div className="sticky top-0 z-10 mb-3 flex items-center gap-2 rounded-lg border border-tracker-cardBorderUnread bg-tracker-statusBg/95 px-3 py-2 text-[11.5px] font-semibold text-tracker-accent shadow-sm backdrop-blur-sm animate-fadeIn">
          <span className="relative h-3.5 w-3.5 flex-none">
            <span className="absolute inset-0 rounded-full border border-tracker-accent/30" />
            <span className="absolute inset-0 rounded-full border border-transparent border-t-tracker-accent animate-spin" />
          </span>
          Updating results…
        </div>
      )}
      {error && (
        <div className="text-[11px] text-rose-600 mb-2">{error}</div>
      )}
      {summary && (
        <div className="text-[11px] text-tracker-muted mb-3">{summary}</div>
      )}
      <div className={`flex flex-col gap-2.5 transition-opacity duration-300 ${loading ? 'opacity-55' : 'opacity-100'}`}>
        {results.map((r) => {
          const registry = registryForNctId(r.nct_id);
          const status = statusColorClasses(r.overall_status || r.status);
          const isSelected = selectedNct === r.nct_id;
          const sponsor = r.organization_full_name || r.sponsor;
          const endpoints = r.primary_outcome_measures || [];
          const interventions = r.intervention_names || [];

          return (
            <div
              key={r.nct_id}
              onClick={() => onSelect(r.nct_id)}
              className={`border rounded-xl px-4 py-3.5 cursor-pointer transition-colors ${
                isSelected
                  ? 'bg-tracker-cardBgSelected border-tracker-cardBorderSelected'
                  : 'bg-white border-tracker-line hover:border-tracker-cardBorderUnread'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className={`flex items-center gap-1 text-[9.5px] font-bold px-1.5 py-0.5 rounded ${registry.bg} ${registry.fg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${registry.dot}`} />
                  {registry.label}
                </span>
                {r.overall_status && (
                  <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${status.bg} ${status.fg}`}>
                    {r.overall_status}
                  </span>
                )}
                {r.phases && (
                  <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-tracker-bg border border-tracker-line text-tracker-ink">
                    {r.phases}
                  </span>
                )}
                {r.from_ctgov && (
                  <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Saved from CT.gov
                  </span>
                )}
              </div>

              <div className="text-[15px] font-semibold leading-snug text-tracker-ink mb-1">
                {r.trial_name || r.full_title || r.nct_id}
              </div>

              {r.full_title && r.full_title !== r.trial_name && (
                <div className="text-[12px] text-tracker-muted leading-snug mb-1.5">{r.full_title}</div>
              )}

              <div className="flex items-center gap-2 text-[11.5px] text-tracker-muted flex-wrap mb-2">
                <span className="font-plex text-tracker-muted2">{r.nct_id}</span>
                {sponsor && onFilterBySponsor && (
                  <>
                    <span>·</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onFilterBySponsor(sponsor); }}
                      className="text-sky-700 hover:text-sky-900 hover:underline font-semibold truncate max-w-[16rem]"
                    >
                      {sponsor}
                    </button>
                  </>
                )}
                {sponsor && !onFilterBySponsor && <><span>·</span><span className="truncate max-w-[16rem]">{sponsor}</span></>}
                {r.enrollment && <><span>·</span><span>n={r.enrollment}</span></>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px] text-tracker-muted mb-2">
                {r.study_start_date && (
                  <div><span className="font-semibold text-tracker-ink/70">Start:</span> {formatDate(r.study_start_date)}</div>
                )}
                {r.primary_completion_date && (
                  <div><span className="font-semibold text-tracker-ink/70">Primary completion:</span> {formatDate(r.primary_completion_date)}</div>
                )}
                {r.study_completion_date && (
                  <div><span className="font-semibold text-tracker-ink/70">Study completion:</span> {formatDate(r.study_completion_date)}</div>
                )}
              </div>

              {interventions.length > 0 && (
                <div className="text-[11px] text-tracker-muted mb-2">
                  <span className="font-semibold text-tracker-ink/70">Interventions:</span>{' '}
                  {interventions.slice(0, 6).join(' · ')}
                  {interventions.length > 6 ? ' …' : ''}
                </div>
              )}

              {endpoints.length > 0 && (
                <div className="mt-1 pt-2 border-t border-tracker-line/80">
                  <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mb-1">PRIMARY ENDPOINTS</div>
                  <ul className="list-disc pl-4 space-y-0.5 text-[11px] text-tracker-ink/90">
                    {endpoints.slice(0, 5).map((ep) => (
                      <li key={ep} className="leading-snug">{ep}</li>
                    ))}
                    {endpoints.length > 5 && (
                      <li className="text-tracker-muted list-none -ml-4">+{endpoints.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}

              {r.brief_summary && (
                <div className="mt-2 text-[11.5px] text-tracker-muted leading-relaxed line-clamp-3">
                  {r.brief_summary}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrackerSearchResults;
